import {
  Box,
  Divider,
  Flex,
  Icon,
  Image,
  InputLeftAddon,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
} from '@chakra-ui/react';
import { format } from 'date-fns-tz';
import { Formik, Form } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { IconCalendar } from 'assets/icons';
import Button from 'components/Button';
import FallbackAvatar from 'components/FallbackAvatar';
import {
  FormikInput,
  FormikTextArea,
  FormikSelect,
  FormikDatepicker,
  FormikSelectAsyncPaginate,
} from 'components/Formik';
import { MONTH_DAY_YEAR_DASH_FORMAT } from 'global/appConstants';
import { IAdjustBalanceFormData, useAdjustBalance } from 'hooks/timeOff/useAdjustBalance';
import { fetchPaginatedCategory } from 'hooks/timeOff/useFetchPaginatedCategories';
import { getAsyncSelectLoadOptions } from 'utils/helpers';

import { initialAdjustBalanceFormData, balanceFormValidationSchema } from './schema';

type TAdjustBalanceModalProps = {
  onClose: () => void;
  data: any;
};

const AdjustBalanceModal: React.FC<TAdjustBalanceModalProps> = ({ onClose, data }) => {
  const { t } = useTranslation(['common', 'timeOff']);

  const { isLoading, mutateAsync } = useAdjustBalance();

  const loadCategoryOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedCategory, {
      page,
      search,
      active: '1',
    });
  };

  const handleFormSubmit = (values: typeof initialAdjustBalanceFormData) => {
    const postData: IAdjustBalanceFormData = {
      user_id: data?.user?.userId,
      category_id: values.category?.id,
      type: values.balance_type?.value,
      duration: values.duration,
      date: values.date ? format(values.date, MONTH_DAY_YEAR_DASH_FORMAT) : undefined,
      description: values.description,
    };

    mutateAsync(postData).then(() => {
      onClose();
    });
  };

  return (
    <>
      <ModalHeader pb="0">Adjust Balance</ModalHeader>
      <ModalBody>
        <Text>
          Use this to adjust the time off balance for this specific user. To change general accrual settings go to Time
          off setup.
        </Text>
        <Divider my="5" borderBottomWidth="2px" borderStyle="dashed" />
        <Formik
          initialValues={{ ...initialAdjustBalanceFormData, category: data?.category || null }}
          validationSchema={balanceFormValidationSchema}
          enableReinitialize
          onSubmit={handleFormSubmit}
        >
          {() => (
            <Form noValidate autoComplete="off">
              <Flex alignItems="center" mb="5">
                <Image
                  src={data?.user?.employee_image}
                  fallback={<FallbackAvatar name={data?.user?.firstName + ' ' + data?.user?.lastName} />}
                  alt={data?.user?.firstName + ' ' + data?.user?.lastName}
                  boxSize="60px"
                  objectFit="cover"
                  borderRadius="lg"
                />
                <Box ml="3" fontSize="md">
                  <Text fontWeight={700} color="newGray.1000">
                    {data?.user?.firstName + ' ' + data?.user?.lastName}
                  </Text>
                  <Text color="gray.500" fontSize="sm">
                    {data?.user?.roleName}
                  </Text>
                </Box>
              </Flex>

              <FormikSelectAsyncPaginate
                name="category"
                label={t('timeOff:timeOffCategory')}
                loadOptions={loadCategoryOptions}
                formControlProps={{ isRequired: true }}
                selectProps={{
                  placeholder: t('timeOff:selectCategory'),
                  isMulti: false,
                  getOptionLabel: (option: any) => option.name,
                  getOptionValue: (option: any) => option.id,
                }}
              />
              <Divider my="5" borderBottomWidth="2px" borderStyle="dashed" />
              <Flex gridGap="4" alignItems="flex-start" mb="5">
                <FormikSelect
                  options={[
                    { label: 'Add', value: 'add' },
                    { label: 'Subtract', value: 'subtract' },
                  ]}
                  name="balance_type"
                  label=""
                  selectProps={{
                    isClearable: false,
                  }}
                  formControlProps={{ isRequired: true, width: '140px', mb: 0 }}
                />
                <FormikInput
                  type="text"
                  name="duration"
                  label=""
                  formControlProps={{ isRequired: true, width: '80px', mb: 0 }}
                />

                <Text mt="4">{data?.category?.inHours ? 'hours' : 'days'} to the balance</Text>
              </Flex>
              <FormikDatepicker
                name="date"
                label="Effective Date"
                formControlProps={{ isRequired: true }}
                datepickerProps={{
                  isClearable: false,
                }}
                inputElement={
                  <InputLeftAddon
                    order={-1}
                    children={
                      <Icon as={IconCalendar} height="20px" width="20px" sx={{ '& path': { fill: 'gray.500' } }} />
                    }
                  />
                }
              />
              <FormikTextArea name="description" label="Comment" formControlProps={{ isRequired: true }} />
              <ModalFooter>
                <Flex justifyContent="flex-end">
                  <Button variant="ghost" onClick={onClose} size="lg" mr="4">
                    {t('common:cancel')}
                  </Button>
                  <Button
                    type="submit"
                    size="lg"
                    fontWeight="semibold"
                    isLoading={isLoading}
                    bg="rgba(72,82, 175, 0.1)"
                    color="primary.500"
                    _hover={{
                      bg: 'rgba(72,82, 175, 0.2)',
                    }}
                    _active={{
                      bg: 'rgba(72,82, 175, 0.2)',
                    }}
                  >
                    {t('common:save')}
                  </Button>
                </Flex>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </>
  );
};

export default AdjustBalanceModal;
